import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    focusInformationAccordion,
    readFocusAccordionFromSearch,
} from "../helpers/accordionDeepLink";

/**
 * Expands and scrolls to an accordion on the Basic Information tab when
 * `focusAccordion` is present in the URL.
 *
 * @param {string} eventKey Accordion event key for this section
 * @param {(key: string|null) => void} setActiveAccordion
 * @param {{ enabled?: boolean, delayMs?: number }} [options]
 */
const useInformationAccordionFocus = (
    eventKey,
    setActiveAccordion,
    { enabled = true, delayMs = 350 } = {}
) => {
    const location = useLocation();

    useEffect(() => {
        if (!enabled || !eventKey) {
            return undefined;
        }

        const { focusAccordion } = readFocusAccordionFromSearch(location.search);

        if (focusAccordion !== eventKey) {
            return undefined;
        }

        setActiveAccordion(eventKey);

        const attemptFocus = (triesLeft) => {
            const focused = focusInformationAccordion(eventKey);

            if (!focused && triesLeft > 0) {
                window.setTimeout(() => attemptFocus(triesLeft - 1), delayMs);
            }
        };

        const timer = window.setTimeout(() => attemptFocus(6), delayMs);

        return () => window.clearTimeout(timer);
    }, [delayMs, enabled, eventKey, location.search, setActiveAccordion]);
};

export default useInformationAccordionFocus;
